import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  SaasProductCreateInput,
  SaasProductDto,
  SaasProductUpdateInput,
} from '@cms/models';
import { saasApi } from '../../../lib/api/saas.api';
import { useAuthStore } from '../../../stores/authStore';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { CommonModal } from '@/components/common/CommonModal';

type ProductFormData = {
  name: string;
  code: string;
  description: string;
  status: string;
};

function nameToCode(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');
}

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y';

export type ProductFormModalMode = 'create' | 'view' | 'edit';

interface ProductFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: ProductFormModalMode;
  /** null when mode is 'create' */
  readonly product: SaasProductDto | null;
  readonly onSuccess?: () => void;
}

export function ProductFormModal({
  open,
  onClose,
  mode,
  product,
  onSuccess,
}: ProductFormModalProps) {
  const tenantId = useAuthStore(s => s.tenantId ?? null);
  const queryClient = useQueryClient();
  const isCreate = mode === 'create';
  const isView = mode === 'view';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      status: 'active',
    },
  });

  const nameValue = watch('name');

  useEffect(() => {
    if (product) {
      reset({
        name: product.name ?? '',
        code: product.code ?? '',
        description: product.description ?? '',
        status: (product.status ?? 'active') as string,
      });
    } else if (isCreate) {
      reset({
        code: '',
        name: '',
        description: '',
        status: 'active',
      });
    }
  }, [product, isCreate, reset]);

  useEffect(() => {
    if (isCreate && nameValue !== undefined) {
      setValue('code', nameToCode(nameValue ?? ''));
    }
  }, [isCreate, nameValue, setValue]);

  const createMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      const code = data.code?.trim() || nameToCode(data.name ?? '');
      const status = data.status === 'inactive' ? 'inactive' : 'active';
      const payload = {
        code: code || 'product',
        name: data.name,
        description: data.description ?? undefined,
        tenantId: tenantId ?? undefined,
        status,
      } as SaasProductCreateInput;
      return saasApi.createSaasProduct(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['saas-products'] });
      onSuccess?.();
      reset();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProductFormData) => {
      if (!product?.id) return Promise.reject(new Error('No product'));
      const payload: SaasProductUpdateInput = {
        name: data.name,
        description: data.description || undefined,
        status: (data.status as 'active' | 'inactive') ?? 'active',
      };
      return saasApi.updateSaasProduct(String(product.id), payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['saas-products'] });
      onSuccess?.();
      onClose();
    },
  });

  const onSubmit = async (data: ProductFormData) => {
    if (isView) return;
    try {
      if (isCreate) {
        await createMutation.mutateAsync(data);
      } else {
        await updateMutation.mutateAsync(data);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : isCreate
            ? 'Failed to create product'
            : 'Failed to update product';
      alert(message);
    }
  };

  const title =
    mode === 'create'
      ? 'Create Product'
      : mode === 'view'
        ? 'Product details'
        : 'Edit product';

  const readOnly = isView;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="product-form-modal-title"
      panelClassName="max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
      >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label
                htmlFor="product-form-name"
                className="block text-sm font-medium text-foreground"
              >
                Name *
              </label>
              <input
                id="product-form-name"
                type="text"
                {...register('name', { required: 'Name is required' })}
                disabled={readOnly}
                className={cn(
                  inputClass,
                  readOnly && 'cursor-default bg-muted/50'
                )}
              />
              {!readOnly && errors.name && (
                <p className="mt-1 text-sm text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>
            {readOnly ? (
              <div>
                <span className="block text-sm font-medium text-muted-foreground mb-1">
                  Status
                </span>
                <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                  {watch('status') || '—'}
                </span>
              </div>
            ) : (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Toggle
                    id="product-form-status"
                    label="Status"
                    checked={field.value === 'active'}
                    onCheckedChange={checked =>
                      field.onChange(checked ? 'active' : 'inactive')
                    }
                  />
                )}
              />
            )}
          </div>

          <div>
            <label
              htmlFor="product-form-code"
              className="block text-sm font-medium text-foreground"
            >
              Code {isCreate ? '(auto from name)' : ''}
            </label>
            <input
              id="product-form-code"
              type="text"
              readOnly
              {...register('code')}
              className={cn(inputClass, 'cursor-default bg-muted/50')}
            />
          </div>

          <div>
            <label
              htmlFor="product-form-description"
              className="block text-sm font-medium text-foreground"
            >
              Description
            </label>
            <textarea
              id="product-form-description"
              rows={3}
              {...register('description')}
              disabled={readOnly}
              className={cn(
                textareaClass,
                readOnly && 'cursor-default bg-muted/50'
              )}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isCreate
                      ? createMutation.isPending
                      : updateMutation.isPending
                  }
                >
                  {isCreate
                    ? createMutation.isPending
                      ? 'Creating…'
                      : 'Create'
                    : updateMutation.isPending
                      ? 'Saving…'
                      : 'Save'}
                </Button>
              </>
            )}
          </div>
      </form>
    </CommonModal>
  );
}
